import { LoadingButton } from '@mui/lab';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { IconChevronDown, IconInfoCircle, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ModalComponents from 'components/ModalComponents';
import SwitchComponent from 'components/SwitchComponent';
import { Form, Formik } from 'formik';
import { get, patch } from 'lib/network/api';
import { errorMsg, successMsg } from 'lib/toastNotification';
import React, { useState } from 'react';
import { useMemo } from 'react';

const NotificationTabs = () => {
  const [modalReset, setModalReset] = useState(false);
  const queryClient = useQueryClient();
  const { data: listNotifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await get('/notification-preference');
      return res.data || [];
    },
    initialData: [],
  });
  const SELF_GROUP = 'self';
  const ONLY_SHOW_NOTIFICATION_ACTIONS = [1004];

  const filteredNotifications = useMemo(() => {
    if (!listNotifications) return [];
    return listNotifications.filter(({ id, group }) => group !== 'Payment Links');
  }, [listNotifications]);

  const groupedNotifications = useMemo(() => {
    if (!filteredNotifications) return {};

    return filteredNotifications.reduce((acc, item) => {
      if (item.is_editable || ONLY_SHOW_NOTIFICATION_ACTIONS.includes(item.notification_action)) {
        const groupKey = item.group || SELF_GROUP;
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push({ ...item });
      }
      return acc;
    }, {});
  }, [filteredNotifications]);

  const mutateNotifications = useMutation({
    mutationFn: (val) => {
      return patch('/notification-preference', val);
    },
    onSuccess: async () => {
      successMsg('Notifications updated successfully');
      await queryClient.invalidateQueries(['notifications']);
      setModalReset(false);
    },
    onError: (err) => {
      errorMsg(err?.detail);
    },
  });

  const handleSubmit = (values) => {
    const payloadArray = Object.entries(values).map(([action, is_active]) => ({
      notification_action: Number(action),
      is_active,
    }));
    mutateNotifications.mutate(payloadArray);
  };
  const handleResetNotif = (values) => {
    if (!filteredNotifications || filteredNotifications.length === 0) {
      errorMsg('No notifications to reset.');
      return;
    }
    const payloadArray = filteredNotifications
      .filter(
        (item) =>
          item.is_editable || ONLY_SHOW_NOTIFICATION_ACTIONS.includes(item.notification_action),
      )
      .map((item) => ({
        notification_action: item.notification_action,
        is_active: true,
      }));
    mutateNotifications.mutate(payloadArray);
  };

  return (
    <Formik
      initialValues={listNotifications.reduce((acc, item) => {
        acc[item.notification_action] = item.is_active;
        return acc;
      }, {})}
      enableReinitialize
      onSubmit={handleSubmit}>
      {({ values, handleChange, dirty }) => (
        <>
          <ModalComponents open={modalReset} onRequestClose={() => setModalReset(false)}>
            <div className="bg-white rounded-md p-4 text-center w-md flex flex-col gap-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg">Reset Notifications</h4>
                <IconX className="cursor-pointer" onClick={() => setModalReset(false)} />
              </div>
              <div className="flex flex-col gap-y-3">
                <div className="flex gap-x-2 p-3 border rounded-lg md:items-center text-start md:text-center">
                  <IconInfoCircle className="w-4 h-4" />
                  <p className="text-gray-900">
                    Disabling notifications may result in missed important updates. We recommend
                    keeping critical notifications enabled.
                  </p>
                </div>
              </div>
              <div className="flex md:justify-end mt-2 ">
                <LoadingButton
                  className="w-full md:w-auto"
                  loading={mutateNotifications.isPending}
                  onClick={() => handleResetNotif(values)}
                  variant="contained">
                  Reset Notifications
                </LoadingButton>
              </div>
            </div>
          </ModalComponents>
          <div className="flex flex-col gap-y-3">
            <div className="flex gap-x-2 p-3 border rounded-lg items-center">
              <IconInfoCircle className="w-4 h-4" />
              <p className="text-gray-900">
                Disabling payment notifications may result in missed important transaction updates.
                We recommend keeping critical notifications enabled.
              </p>
            </div>
            <Form>
              <div>
                <div className="rounded-lg border flex flex-col gap-y-2 p-4">
                  <div className="flex flex-col gap-y-1">
                    <h4 className="text-gray-900 font-semibold text-lg">
                      Notification Preferences
                    </h4>
                    <span className="text-gray-400">
                      Choose which email notifications you want to receive. Security-related emails
                      cannot be disabled.
                    </span>
                  </div>
                  {Object.entries(groupedNotifications).map(([groupName, items], index) => (
                    <div className="m-0 p-0 border-b last:border-none pb-1" key={index}>
                      {items.length >= 1 && groupName !== SELF_GROUP ? (
                        <Accordion
                          sx={{
                            boxShadow: 'none',
                            margin: 0,
                          }}>
                          <AccordionSummary
                            aria-controls="panel1-content"
                            id="panel1-header"
                            sx={{
                              padding: '0 !important',
                              margin: 0,
                            }}>
                            <div>
                              <div className="flex items-center gap-x-1">
                                <h6 className="text-sm font-semibold text-gray-900 ">
                                  {groupName}
                                </h6>
                                <IconChevronDown className="w-5" />
                              </div>
                              <span className="text-gray-400">
                                {groupName} for different payment methods
                              </span>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails sx={{ paddingBottom: 0, paddingTop: 0 }}>
                            {items.map((dataItem, keyItem) => (
                              <div
                                className="flex justify-between my-3  border-l-2 border-gray-200 ml-0 md:ml-3 pl-3"
                                key={keyItem}>
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {dataItem?.notification_action_label}
                                  </p>
                                  <span className="text-gray-400 text-sm">
                                    {dataItem?.description !== '' ? dataItem?.description : '-'}
                                  </span>
                                </div>
                                <SwitchComponent
                                  onChange={handleChange}
                                  name={dataItem.notification_action.toString()}
                                  id={dataItem.notification_action.toString()}
                                  checked={!!values[dataItem.notification_action]}
                                  disabled={!dataItem?.is_editable}
                                />
                              </div>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      ) : (
                        <div>
                          {items?.map((dataItem, keyItem) => {
                            // hide notification action 1010 --> download data
                            if (dataItem?.notification_action !== 1010) {
                              return (
                                <div className="flex justify-between my-3 pr-3" key={keyItem}>
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {dataItem?.notification_action_label}
                                    </p>
                                    <span className="text-gray-400 text-sm">
                                      {dataItem?.description !== '' ? dataItem?.description : '-'}
                                    </span>
                                  </div>
                                  <SwitchComponent
                                    checked={!!dataItem?.is_active}
                                    disabled={!dataItem?.is_editable}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col lg:flex-row items-center justify-between mt-8 gap-y-4">
                  <LoadingButton
                    variant="outlined"
                    size="small"
                    className="hover:!bg-info-main hover:text-white w-full lg:w-fit"
                    onClick={() => setModalReset(true)}>
                    Reset to Default
                  </LoadingButton>
                  <div className="flex flex-col lg:flex-row items-center gap-x-2 w-full lg:w-fit">
                    {dirty && <p>You have unsaved changes</p>}
                    <LoadingButton
                      className=" w-full lg:w-fit"
                      variant="contained"
                      size="small"
                      type="submit"
                      loading={mutateNotifications.isPending}
                      disabled={!dirty}>
                      Save Changes
                    </LoadingButton>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </>
      )}
    </Formik>
  );
};

export default NotificationTabs;
